import React, { useState, useEffect } from "react";
import AnimatedCircularProgress from "./AnimatedCircularProgress";
import FeedbackPopup from "./FeedbackPopup";
import AiEvalBreakdownPopup from "./common/AiEvalBreakdownPopup";
import { checkIsAdmin, checkIsAiEvalEnabled } from "../helper/authUtils";
import { getUserAiEvalTest, getUserAiEvalDetails, regenerateUserAiEvalTest } from "../helper/apicalls/user";

import "../styles/popup.scss";
import "../styles/MixedContent.scss";
import "../styles/TestPage.scss";
import "../styles/HomeworkFeedback.scss";

const HomeworkFeedback = ({
  homeworkMetadata,
  score,
  totalQuestions,
  feedbackItems = [],
  onShowAnswers,
  onBack,
}) => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [homeworkEvals, setHomeworkEvals] = useState({});
  const [evalPopupOpen, setEvalPopupOpen] = useState(false);
  const [selectedEvalData, setSelectedEvalData] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [isRegeneratingHomework, setIsRegeneratingHomework] = useState(false);

  const getHomeworkKey = () => {
    return homeworkMetadata?.id || new URLSearchParams(window.location.search).get("homework_id");
  };

  const parseScore = (val) => {
    if (val === undefined || val === null) return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
  };

  const parseJsonOrObject = (val) => {
    if (!val) return {};
    if (typeof val === "object") return val;
    try {
      return JSON.parse(val);
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    if (!checkIsAiEvalEnabled() || checkIsAdmin()) return;

    let isMounted = true;
    let intervalId = null;
    let attempts = 0;
    const maxAttempts = 3;

    const doFetch = async () => {
      const key = getHomeworkKey();
      if (!key) return false;
      try {
        const res = await getUserAiEvalTest({ homework_completed_id: key });
        if (!isMounted) return false;
        if (res && res.success && res.evaluations) {
          const parsedEvals = {};
          Object.entries(res.evaluations).forEach(([id, evalItem]) => {
            parsedEvals[id] = {
              overall_score: parseScore(evalItem.overall_score),
              evalution_score: parseJsonOrObject(evalItem.evalution_score)
            };
          });
          setHomeworkEvals((prev) => ({ ...prev, ...parsedEvals }));
          if (parsedEvals[key]) {
            return true;
          }
        }
      } catch (err) {
        console.error("Fetch homework evaluations failed:", err);
      }
      return false;
    };

    const runPoll = async () => {
      const success = await doFetch();
      if (success) return;

      intervalId = setInterval(async () => {
        attempts += 1;
        const ok = await doFetch();
        if (ok || attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, 5000);
    };

    runPoll();

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [homeworkMetadata]);

  const handleHomeworkAiEvalClick = async () => {
    const key = getHomeworkKey();
    if (!key) return;
    if (homeworkEvals[key]) {
      setSelectedEvalData(homeworkEvals[key]);
      setEvalPopupOpen(true);
      return;
    }
    setEvalLoading(true);
    try {
      const data = await getUserAiEvalDetails("homework", key);
      if (data && data.is_evaluated && data.data) {
        const rec = typeof data.data === "string" ? JSON.parse(data.data) : data.data;
        const parsed = {
          overall_score: parseScore(rec.overall_score),
          evalution_score: parseJsonOrObject(rec.evalution_score)
        };
        setHomeworkEvals((prev) => ({ ...prev, [key]: parsed }));
        setSelectedEvalData(parsed);
      } else {
        setSelectedEvalData(data);
      }
      setEvalPopupOpen(true);
    } catch (err) {
      console.error("Failed to fetch AI evaluation details for homework:", err);
    } finally {
      setEvalLoading(false);
    }
  };

  const handleRegenerateHomeworkEval = async () => {
    const key = getHomeworkKey();
    if (!key) return;
    setIsRegeneratingHomework(true);
    setEvalLoading(true);
    setHomeworkEvals((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    try {
      const res = await regenerateUserAiEvalTest({ homework_completed_id: key });
      setIsRegeneratingHomework(false);
      if (res && res.success) {
        if (res.evaluation) {
          const evalObj = {
            overall_score: parseScore(res.evaluation.overall_score),
            evalution_score: parseJsonOrObject(res.evaluation.evalution_score),
          };
          setHomeworkEvals((prev) => ({
            ...prev,
            [key]: evalObj,
          }));
          setEvalLoading(false);
        } else {
          let attempts = 0;
          const pollInterval = setInterval(async () => {
            attempts++;
            try {
              const evalRes = await getUserAiEvalTest({ homework_completed_id: key });
              const evalItem = evalRes?.evaluation || (evalRes?.evaluations && evalRes.evaluations[key]);
              if (evalRes?.success && evalItem) {
                setHomeworkEvals((prev) => ({
                  ...prev,
                  [key]: {
                    overall_score: parseScore(evalItem.overall_score),
                    evalution_score: parseJsonOrObject(evalItem.evalution_score),
                  },
                }));
                setEvalLoading(false);
                clearInterval(pollInterval);
              }
            } catch (e) {
              console.error("Error polling homework evaluation:", e);
            }
            if (attempts >= 10) {
              setEvalLoading(false);
              clearInterval(pollInterval);
            }
          }, 3000);
        }
      } else {
        setEvalLoading(false);
      }
    } catch (err) {
      console.error("Failed to regenerate homework AI evaluation:", err);
      setIsRegeneratingHomework(false);
      setEvalLoading(false);
    }
  };


  const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;

  const getBadgeClassByLevel = (level) => {
    if (!level) return "default";
    const lvl = level.toLowerCase();
    if (lvl.includes("needs") || lvl.includes("improvement") || lvl.includes("poor")) {
      return "needs-improvement";
    }
    if (lvl.includes("good") || lvl.includes("average") || lvl.includes("satisfactory")) {
      return "good";
    }
    if (lvl.includes("excellent") || lvl.includes("outstanding") || lvl.includes("great")) {
      return "excellent";
    }
    return "default";
  };

  const overallPerformanceLevel = feedbackItems && feedbackItems.length > 0
    ? feedbackItems[0]?.performance_level
    : (percentage < 50 ? "Needs Improvement" : percentage <= 75 ? "Good" : "Excellent");

  const overallBadgeClass = getBadgeClassByLevel(overallPerformanceLevel);

  const getPerformanceWrapperClassByBadge = (badge) => {
    if (badge === "needs-improvement") return "score-feedback-wrapper--needs-improvement";
    if (badge === "good") return "score-feedback-wrapper--good";
    if (badge === "excellent") return "score-feedback-wrapper--excellent";
    return "score-feedback-wrapper--default";
  };

  const getFeedbackSectionClassByBadge = (badge) => {
    if (badge === "needs-improvement") return "feedback-section--needs-improvement";
    if (badge === "good") return "feedback-section--good";
    if (badge === "excellent") return "feedback-section--excellent";
    return "feedback-section--default";
  };

  return (
    <div className="homework-feedback-container custom-scrollbar">
      <div className="homework-feedback-card">
        <div className="homework-feedback-header">
          <h2>{homeworkMetadata?.name || "Homework Results"}</h2>
        </div>

        <div className="homework-feedback-body">
          <div
            className={`score-feedback-wrapper custom-scrollbar ${getPerformanceWrapperClassByBadge(
              overallBadgeClass
            )}`}
          >
            <div className="score-chart-container">
              <div className="score-chart-wrapper">
                <AnimatedCircularProgress score={percentage} />
              </div>
            </div>

            {feedbackItems && feedbackItems.length > 0 && (
              <div
                className={`feedback-section ${getFeedbackSectionClassByBadge(
                  overallBadgeClass
                )}`}
              >
                {feedbackItems.map((fb, index) => {
                  const itemBadgeClass = getBadgeClassByLevel(fb.performance_level);
                  return (
                    <div key={index} className="feedback-item">
                      <div className="feedback-header">
                        <div className={`performance-badge ${itemBadgeClass}`}>
                          {fb.performance_level || overallPerformanceLevel}
                        </div>
                        {(fb.improvement_advice || fb.title || fb.content) && (
                          <button
                            className="additional-feedback-btn"
                            onClick={() => setShowFeedbackModal(true)}
                          >
                            Additional Feedback
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="homework-feedback-footer">
          <button className="btn-secondary" onClick={onBack}>
            Go back to all homeworks
          </button>
          <button className="btn-primary btn-show-answer" onClick={onShowAnswers}>
            Show Answer
          </button>
        </div>
      </div>

      <FeedbackPopup
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        feedbackItems={feedbackItems}
        percentage={percentage}
        isAiEvalEnabled={checkIsAiEvalEnabled()}
        isAdmin={checkIsAdmin()}
        aiEvalData={homeworkEvals[getHomeworkKey()]}
        onAiChipClick={() => {
          setShowFeedbackModal(false);
          handleHomeworkAiEvalClick();
        }}
        onRegenerate={handleRegenerateHomeworkEval}
        isRegenerating={isRegeneratingHomework}
        isEvaluating={evalLoading}
      />

      <AiEvalBreakdownPopup
        open={evalPopupOpen}
        onClose={() => setEvalPopupOpen(false)}
        evalData={selectedEvalData}
      />
    </div>
  );
};

export default HomeworkFeedback;
